/**
 * The chat on the main map (port addition; the controller: src/game/runtime/chat.ts; the backend: protocol chat.*):
 *  - the chat button ('chat_open'): a round button of the main map with the unread badge, in the HUD's bottom right
 *    corner on a wide screen (above the version label: ui/wide-screen.ts group 'chat', clear of the plates, Papa John,
 *    the quests, music and sound, the auction and the scene's key objects), under the gold plate's buttons on a
 *    desktop; shown only while the backend offers the chat and a player is signed in;
 *  - the chat panel: a modal popup over the dimmed main map in the colours of the game's popups (the cream edge, the
 *    light green frame, the pale body, the beanstalk and the bones of menu_tired, its close button): the tabs
 *    "Общий" / "Клан" (the clan's tab only for a member of a clan; each tab with its unread count), the messages
 *    (oldest at the top, newest at the bottom; scrolled with the wheel or a finger; older pages load at the top), the
 *    input field with the character counter and "Отправить". A wide host (a phone, a tablet) lays the panel out in CSS
 *    px over the shown safe area: 48 CSS px targets, 14 CSS px text; the field and "Отправить" go above an on-screen
 *    keyboard (the host's keyboard view: TextField.$keyboardWith). A desktop gets a 640 x 440 panel in the window.
 *  - a tap on another player's message offers to hide that player's messages on this device (the controller's list;
 *    "Скрытые игроки" shows them again);
 *  - guests read only (the field is replaced by a note), so do muted players and those below the level to write;
 *  - Escape, the Android back button (backAction) and the close button close it; it closes by itself when the main
 *    map is no longer the top screen (a battle, another screen): the chat never lies over a battle.
 * Messages, nicknames and clan names are data: shown as plain text (setRawText), never translated, never markup.
 */
import { Event, KeyboardEvent, MouseEvent, Point, Rectangle, Shape, SimpleButton, Sprite, TextField, TextFormat } from '@flash';
import { CHAT_GLOBAL, CHAT_TEXT_MAX, chatTextLength, sanitizeChatText } from '../../shared/chat-rules';
import type { ChatChannelInfo, ChatController, ChatLine as ChatMessage } from '../runtime/chat';
import * as T from '../runtime/chat-texts';
import { compose } from '../runtime/clan-texts';
import { playerName, translate } from '../runtime/i18n';
import { netErrorCode } from '../runtime/net-policy';
import { ERROR_COLOR, HINT_COLOR, LINK_COLOR, TEXT_COLOR, inputField, setRawText } from './controls';

// ------------------------------------------------------------------------------------------------ colours and sizes

/** The game's popup colours (menu_tired's frame): the cream edge, its light green border, the pale body. */
const EDGE = 0xfffcc8;
const BORDER_MID = 0xdaeba0;
const BORDER = 0xb8db7c;
const BODY = 0xe9ebbf;
const LIST_BG = 0xf6f7dc;
/** The main map's plates: the dark green box, its darker edge, the light green caption. */
const PLATE = 0x3b4f3e;
const PLATE_OVER = 0x4c6650;
const PLATE_EDGE = 0x2a392d;
const PLATE_CAPTION = 0xd4f0a0;
/** "Отправить": the green of the popups' big button. */
const GO = 0x14d24a;
const GO_OVER = 0x30e862;
const GO_EDGE = 0x0b7a2a;
const GO_CAPTION = 0x14301a;
const BADGE = 0xe0262b;
const NICK = 0x2d6418;
const OWN_NICK = 0x9a5200;
const META = 0x707060;

export const CHAT_BUTTON_NAME = 'chat_open';
export const CHAT_SEND_NAME = 'chat_send';
export const CHAT_INPUT_NAME = 'chat_input';
const ROOT_NAME = '$chat_panel';
const BUTTON_KEY = '$chat_button';

/** The chat button as the original's window lays it out on a desktop: under the gold plate's buttons, in the sky. */
export const CHAT_BUTTON_BOX = { x: 752, y: 128, width: 44, height: 44 };

/** Older messages are asked for when the list is scrolled this close to its top. */
const LOAD_OLDER_AT = 40;
/** A tap that moved less than this (panel units) is a tap, more is a drag of the list. */
const DRAG_SLOP = 8;
/** The read position is sent this long after the newest shown message changed. */
const MARK_READ_MS = 1_500;
/** Messages a channel's list keeps when the server does not tell its history (limits.chat). */
const CHAT_KEPT_DEFAULT = 200;

/** The panel's units: on a wide host 1 unit = 1 CSS px (`u` stage px), on a desktop 1 stage px. */
interface Layout {
  wide: boolean;
  /** Stage px per unit. */
  u: number;
  /** The panel's place (stage px) and size (units). */
  x: number;
  y: number;
  w: number;
  h: number;
  /** Text sizes (units). */
  text: number;
  nick: number;
  meta: number;
  /** Heights (units): the header row, the input row, a button. */
  header: number;
  row: number;
}

function layoutFor(stage: any): Layout {
  const wide = !!(stage && stage.$visibleArea && Number(stage.cssScale) > 0);
  if (!wide) {
    const w = 640;
    const h = 440;
    return { wide, u: 1, x: Math.round((807 - w) / 2), y: Math.round((600 - h) / 2), w, h, text: 13, nick: 12, meta: 10, header: 44, row: 40 };
  }
  const css = Number(stage.cssScale);
  const s = stage.safeRect;
  const u = 1 / css;
  const w = Math.round(Math.min(s.width * css - 16, 780));
  const h = Math.round(Math.min(s.height * css - 12, 560));
  return { wide, u, x: s.x + (s.width - w * u) / 2, y: s.y + (s.height - h * u) / 2, w, h, text: 14, nick: 13, meta: 11, header: 52, row: 48 };
}

// ------------------------------------------------------------------------------------------------ controls

function rect(g: any, color: number, x: number, y: number, w: number, h: number, r = 0, alpha = 1): void {
  g.beginFill(color, alpha);
  if (r > 0) g.drawRoundRect(x, y, w, h, r, r);
  else g.drawRect(x, y, w, h);
  g.endFill();
}

function textFormat(size: number, color: number, bold = false, align?: string): TextFormat {
  const f = new TextFormat('Arial', size, color, bold);
  if (align) f.align = align;
  return f;
}

/** A one-line text that never takes the mouse (the caption of a button, a nickname). */
function line(size: number, color: number, bold = false): TextField {
  const tf = new TextField();
  tf.defaultTextFormat = textFormat(size, color, bold);
  tf.selectable = false;
  tf.mouseEnabled = false;
  tf.autoSize = 'left';
  return tf;
}

/** A word-wrapped text of `width` units (a message, a note). */
function block(size: number, color: number, width: number, bold = false, align?: string): TextField {
  const tf = new TextField();
  tf.defaultTextFormat = textFormat(size, color, bold, align);
  tf.selectable = false;
  tf.mouseEnabled = false;
  tf.multiline = true;
  tf.wordWrap = true;
  tf.width = width;
  tf.height = size * 1.3 + 4;
  return tf;
}

/** Fits a word-wrapped field's height to its text. */
function fitHeight(tf: TextField): void {
  tf.height = Math.ceil(tf.textHeight + 5);
}

type PlateStyle = 'dark' | 'light' | 'go';

const STYLES: Record<PlateStyle, { fill: number; over: number; edge: number; caption: number }> = {
  dark: { fill: PLATE, over: PLATE_OVER, edge: PLATE_EDGE, caption: PLATE_CAPTION },
  light: { fill: BORDER, over: 0xc9e894, edge: 0x82a852, caption: PLATE_EDGE },
  go: { fill: GO, over: GO_OVER, edge: GO_EDGE, caption: GO_CAPTION },
};

/**
 * A button of the panel: a plate in the main map's colours with a caption (a SimpleButton, so the touch assist and
 * the hover work as for the game's buttons) and an optional count badge.
 */
export class PlateButton extends Sprite {
  readonly button: SimpleButton;
  readonly caption: TextField;
  private style: PlateStyle;
  private w: number;
  private h: number;
  private text = '';
  private raw = false;
  private badge: Sprite | null = null;
  private active = true;
  /** The tap area beyond the plate (setHitRect), kept when the plate is drawn anew. */
  private hitRect: { x: number; y: number; width: number; height: number } | null = null;

  constructor(name: string, text: string, opts: { w: number; h: number; size: number; style?: PlateStyle; raw?: boolean }) {
    super();
    this.name = name;
    this.style = opts.style ?? 'dark';
    this.w = opts.w;
    this.h = opts.h;
    this.button = new SimpleButton();
    this.button.name = `${name}_btn`;
    this.addChild(this.button);
    this.caption = line(opts.size, STYLES[this.style].caption, true);
    this.addChild(this.caption);
    this.raw = !!opts.raw;
    this.draw();
    this.setText(text, this.raw);
  }

  private state(color: number, dy = 0): Shape {
    const s = new Shape();
    const st = STYLES[this.style];
    rect(s.graphics, st.edge, 0, dy, this.w, this.h, 12);
    rect(s.graphics, color, 2, 2 + dy, this.w - 4, this.h - 4, 9);
    return s;
  }

  private draw(): void {
    const st = STYLES[this.style];
    this.button.upState = this.state(st.fill);
    this.button.overState = this.state(st.over);
    this.button.downState = this.state(st.fill, 1);
    const r = this.hitRect ?? { x: 0, y: 0, width: this.w, height: this.h };
    const hit = new Shape();
    rect(hit.graphics, 0, r.x, r.y, r.width, r.height, 0, 0);
    this.button.hitTestState = hit;
  }

  /** The caption (game text, translated; `raw`: data such as a count, shown as it is), shrunk to the plate's width. */
  setText(text: string, raw = this.raw): void {
    this.text = text;
    this.raw = raw;
    const f = this.caption.defaultTextFormat;
    let size = Number(f.size);
    const apply = () => {
      const ff = this.caption.defaultTextFormat;
      ff.size = size;
      ff.color = STYLES[this.style].caption;
      this.caption.defaultTextFormat = ff;
      if (raw) setRawText(this.caption, text);
      else this.caption.text = text;
    };
    apply();
    while (this.caption.textWidth > this.w - 16 && size > 9) {
      size -= 1;
      apply();
    }
    this.caption.x = Math.round((this.w - this.caption.width) / 2);
    this.caption.y = Math.round((this.h - this.caption.height) / 2);
  }

  get label(): string {
    return this.text;
  }

  setStyle(style: PlateStyle): void {
    if (style === this.style) return;
    this.style = style;
    this.draw();
    this.setText(this.text, this.raw);
  }

  /** The taps of `r` (this control's coordinates) reach the button: a finger's target larger than its plate. */
  setHitRect(r: { x: number; y: number; width: number; height: number }): void {
    this.hitRect = { ...r };
    this.draw();
  }

  /** A red count at the plate's top right corner (0: none). */
  setBadge(n: number, size: number): void {
    if (this.badge) this.removeChild(this.badge);
    this.badge = n > 0 ? badgeOf(n, size) : null;
    if (this.badge) {
      this.badge.x = this.w - this.badge.width * 0.6;
      this.badge.y = -this.badge.height * 0.35;
      this.addChild(this.badge);
    }
  }

  get enabled(): boolean {
    return this.active;
  }

  set enabled(on: boolean) {
    this.active = on;
    this.mouseEnabled = on;
    this.mouseChildren = on;
    this.button.enabled = on;
    this.alpha = on ? 1 : 0.5;
  }
}

/** The red badge of an unread count ("99+" above 99). */
function badgeOf(n: number, size: number): Sprite {
  const s = new Sprite();
  s.name = 'chat_badge';
  s.mouseEnabled = false;
  s.mouseChildren = false;
  const tf = line(size, 0xffffff, true);
  setRawText(tf, n > 99 ? '99+' : String(n));
  const h = Math.round(size * 1.55);
  const w = Math.max(h, Math.round(tf.textWidth + size));
  rect(s.graphics, 0xffffff, 0, 0, w, h, h);
  rect(s.graphics, BADGE, 1.5, 1.5, w - 3, h - 3, h - 3);
  tf.x = Math.round((w - tf.width) / 2);
  tf.y = Math.round((h - tf.height) / 2);
  s.addChild(tf);
  return s;
}

/** The chat button's round art: the plates' dark green ring, the cream disc, a speech bubble with three dots. */
function bubbleArt(pressed: boolean, over: boolean): Shape {
  const s = new Shape();
  const g = s.graphics;
  const r = CHAT_BUTTON_BOX.width / 2;
  const dy = pressed ? 1 : 0;
  g.beginFill(PLATE_EDGE, 1);
  g.drawCircle(r, r + dy, r);
  g.endFill();
  g.beginFill(over ? 0xfffff0 : EDGE, 1);
  g.drawCircle(r, r + dy, r - 3);
  g.endFill();
  // the bubble and its tail
  g.beginFill(PLATE, 1);
  g.drawRoundRect(9, 11 + dy, 26, 18, 10, 10);
  g.endFill();
  g.beginFill(PLATE, 1);
  g.moveTo(14, 27 + dy);
  g.lineTo(12, 34 + dy);
  g.lineTo(21, 28 + dy);
  g.endFill();
  g.beginFill(PLATE_CAPTION, 1);
  for (const x of [16, 22, 28]) g.drawCircle(x, 20 + dy, 2.2);
  g.endFill();
  return s;
}

export interface ChatButton extends Sprite {
  button: SimpleButton;
  shownUnread: number;
}

/**
 * Adds the chat button to a rendered main map (idempotent) and shows it while the chat is available; every frame it
 * lets the controller ask for the unread counts when they are due and shows them.
 */
export function chatButton(screen: any, fwp: any): ChatButton | null {
  if (!screen || typeof screen.addChild !== 'function' || !fwp || !fwp.chat) return null;
  const ctl: ChatController = fwp.chat;
  let b = screen[BUTTON_KEY] as ChatButton | undefined;
  if (!b) {
    b = new Sprite() as ChatButton;
    b.name = CHAT_BUTTON_NAME;
    b.shownUnread = -1;
    const hit = new Shape();
    hit.graphics.beginFill(0, 0);
    hit.graphics.drawCircle(CHAT_BUTTON_BOX.width / 2, CHAT_BUTTON_BOX.height / 2, CHAT_BUTTON_BOX.width / 2);
    hit.graphics.endFill();
    b.button = new SimpleButton(bubbleArt(false, false), bubbleArt(false, true), bubbleArt(true, true), hit);
    b.button.name = 'chat_open_btn';
    b.addChild(b.button);
    b.x = CHAT_BUTTON_BOX.x;
    b.y = CHAT_BUTTON_BOX.y;
    const button = b;
    b.addEventListener(MouseEvent.CLICK, () => fwp.net_chat_open());
    b.addEventListener(Event.ENTER_FRAME, () => {
      if (!button.stage || !button.visible) return;
      ctl.tick();
      const n = ctl.unread;
      if (n !== button.shownUnread) {
        button.shownUnread = n;
        const old = button.getChildByName('chat_badge');
        if (old) button.removeChild(old);
        if (n > 0) {
          const badge = badgeOf(n, 11);
          badge.x = CHAT_BUTTON_BOX.width - badge.width * 0.7;
          badge.y = -4;
          button.addChild(badge);
        }
      }
    });
    screen.addChild(b);
    screen[BUTTON_KEY] = b;
    // The counts of the badge are asked for again when the main map is drawn anew.
    ctl.refreshSoon();
  }
  b.visible = ctl.available();
  return b;
}

// ------------------------------------------------------------------------------------------------ the panel

interface ChannelView {
  info: ChatChannelInfo;
  /** Loaded messages, oldest first. */
  messages: ChatMessage[];
  /** The cursor of the older page (null: nothing older), undefined before the first page. */
  older: string | null | undefined;
  loading: boolean;
  failed: boolean;
  /** Messages that arrived while another tab was shown. */
  fresh: number;
}

interface Row {
  sprite: Sprite;
  message: ChatMessage;
  height: number;
}

type Queued = { kind: 'message'; message: ChatMessage } | { kind: 'deleted'; channel: string; ids: string[] } | { kind: 'membership' } | { kind: 'status'; status: string };

export interface ChatPanelOptions {
  onClosed(): void;
  /** The session ended meanwhile: the game handles it like any ended session. */
  onSessionEnded?(): void;
}

export class ChatPanel {
  readonly root = new Sprite();
  private readonly body = new Sprite();
  private lay: Layout;
  private disposed = false;
  private stageRef: any = null;
  private readonly views = new Map<string, ChannelView>();
  private order: string[] = [CHAT_GLOBAL];
  current = CHAT_GLOBAL;
  /** What the panel shows: the messages, or the list of hidden players. */
  mode: 'messages' | 'hidden' = 'messages';
  busy = false;
  /** Requests sent (tests). */
  sent = 0;
  private queue: Queued[] = [];
  private offs: Array<() => void> = [];
  private status: { text: string; error: boolean; until: number } | null = null;
  private selected: ChatMessage | null = null;
  private markTimer: { channel: string; seq: number; at: number } | null = null;
  private readSent = new Map<string, number>();
  /** A finger (or the mouse button) is down on the stage: the controls stay as they are until it is lifted. */
  private pressing = false;
  /**
   * A re-layout held back while pressing (a control rebuilt under the finger loses its tap: the press on "Отправить"
   * ends the keyboard's view, whose resize would rebuild the row): 'view' if the shown area still asks for it, 'always'
   * (the tabs changed); done at the first frame after the release.
   */
  private held: 'view' | 'always' | null = null;

  // controls (rebuilt by layout())
  private tabs: PlateButton[] = [];
  private hiddenButton: PlateButton | null = null;
  private closeButton: any = null;
  private list = new Sprite();
  private listBg = new Sprite();
  private content = new Sprite();
  private scrollBar = new Shape();
  private newPill: PlateButton | null = null;
  input!: TextField;
  private fieldBox = new Sprite();
  private fieldW = 0;
  private fieldH = 0;
  private fieldFocused = false;
  private placeholder!: TextField;
  private counter!: TextField;
  private statusField!: TextField;
  private note!: TextField;
  sendButton!: PlateButton;
  private hideButton!: PlateButton;
  private cancelButton!: PlateButton;
  private hiddenPage = new Sprite();
  private rows: Row[] = [];
  private rowCache = new Map<string, Row>();
  private contentHeight = 0;
  private scrollY = 0;
  private listX = 0;
  private listY = 0;
  private listW = 0;
  private listH = 0;
  private atBottom = true;
  private unseen = 0;
  private drag: { y: number; scroll: number; moved: boolean } | null = null;
  private typed = '';

  constructor(
    private readonly fwp: any,
    private readonly ctl: ChatController,
    private readonly opts: ChatPanelOptions,
  ) {
    this.root.name = ROOT_NAME;
    const dim = new Sprite();
    dim.name = '$chat_dim';
    rect(dim.graphics, 0x000000, -2000, -1000, 4807, 2600, 0, 0.45);
    // (the dim takes the taps around the panel: nothing of the main map under it answers while the chat is open)
    dim.addEventListener(MouseEvent.CLICK, () => undefined);
    this.root.addChild(dim);
    this.body.name = '$chat_body';
    this.root.addChild(this.body);
    this.lay = layoutFor(null);
    for (const c of this.ctl.channels) this.viewOf(c);
    this.layout();
    this.root.addEventListener(Event.ENTER_FRAME, this.onFrame);
    const s = this.services;
    if (s && typeof s.on === 'function') {
      this.offs.push(s.on('chat.message', (p: any) => p && p.message && this.queue.push({ kind: 'message', message: p.message })));
      this.offs.push(s.on('chat.deleted', (p: any) => p && this.queue.push({ kind: 'deleted', channel: String(p.channel), ids: Array.isArray(p.messageIds) ? p.messageIds.map(String) : [] })));
      this.offs.push(s.on('clan.membership', () => this.queue.push({ kind: 'membership' })));
    }
    if (s && typeof s.onConnectionStatus === 'function') this.offs.push(s.onConnectionStatus((status: string) => this.queue.push({ kind: 'status', status })));
    if (s && typeof s.onEventGap === 'function') this.offs.push(s.onEventGap(() => this.queue.push({ kind: 'status', status: 'open' })));
    this.refreshChannels(true);
  }

  private get services(): any {
    return this.fwp.services;
  }

  // ------------------------------------------------------------------------------------------------ lifecycle

  attach(): void {
    const st = this.root.stage;
    if (!st || this.stageRef) return;
    this.stageRef = st;
    st.addEventListener(KeyboardEvent.KEY_DOWN, this.onKey);
    st.addEventListener(Event.RESIZE, this.onResize);
    st.addEventListener(MouseEvent.MOUSE_MOVE, this.onMove);
    st.addEventListener(MouseEvent.MOUSE_UP, this.onUp);
    // (both phases: a press on a control reaches the stage's capture listener, one on nothing the stage itself)
    st.addEventListener(MouseEvent.MOUSE_DOWN, this.onPress, true);
    st.addEventListener(MouseEvent.MOUSE_DOWN, this.onPress);
    st.addEventListener(MouseEvent.MOUSE_UP, this.onRelease, true);
    st.addEventListener(MouseEvent.MOUSE_UP, this.onRelease);
    this.layout();
  }

  /** A phone without the wide view zooms into the panel (main.ts focusRect); a wide screen shows it at its size. */
  focusRect(): { x: number; y: number; width: number; height: number } | null {
    if (this.disposed || !this.root.stage || this.lay.wide) return null;
    return { x: this.lay.x, y: this.lay.y, width: this.lay.w, height: this.lay.h };
  }

  back(): boolean {
    if (this.disposed) return true;
    if (this.selected) this.select(null);
    else if (this.mode === 'hidden') this.showMode('messages');
    else this.close();
    return true;
  }

  close(): void {
    if (this.disposed) return;
    this.dispose();
    this.opts.onClosed();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const off of this.offs) {
      try {
        off();
      } catch {
        /* ignore */
      }
    }
    this.offs = [];
    if (this.stageRef) {
      this.stageRef.removeEventListener(KeyboardEvent.KEY_DOWN, this.onKey);
      this.stageRef.removeEventListener(Event.RESIZE, this.onResize);
      this.stageRef.removeEventListener(MouseEvent.MOUSE_MOVE, this.onMove);
      this.stageRef.removeEventListener(MouseEvent.MOUSE_UP, this.onUp);
      this.stageRef.removeEventListener(MouseEvent.MOUSE_DOWN, this.onPress, true);
      this.stageRef.removeEventListener(MouseEvent.MOUSE_DOWN, this.onPress);
      this.stageRef.removeEventListener(MouseEvent.MOUSE_UP, this.onRelease, true);
      this.stageRef.removeEventListener(MouseEvent.MOUSE_UP, this.onRelease);
      if (this.stageRef.focus === this.input) this.stageRef.focus = null;
    }
    this.stageRef = null;
    this.root.removeEventListener(Event.ENTER_FRAME, this.onFrame);
    this.flushRead();
    // Live messages only while the chat is shown.
    const s = this.services;
    if (s && typeof s.chatUnsubscribe === 'function') {
      const done: Promise<unknown> = Promise.resolve(s.chatUnsubscribe()).catch(() => undefined);
      this.ctl.unsubscribing = done;
      void done.then(() => {
        if (this.ctl.unsubscribing === done) this.ctl.unsubscribing = null;
      });
    }
    if (this.root.parent) this.root.parent.removeChild(this.root);
    this.ctl.refreshSoon();
  }

  get isDisposed(): boolean {
    return this.disposed;
  }

  // ------------------------------------------------------------------------------------------------ layout

  private readonly onResize = (): void => {
    const st = this.root.stage;
    // (the keyboard's view zooms to the field: the panel keeps its layout under it)
    if (!st || st.$keyboardView) return;
    const next = layoutFor(st);
    if (next.u === this.lay.u && next.w === this.lay.w && next.h === this.lay.h && next.x === this.lay.x && next.y === this.lay.y) return;
    if (this.pressing) this.held = this.held ?? 'view';
    else this.layout();
  };

  private readonly onPress = (): void => {
    this.pressing = true;
  };

  private readonly onRelease = (): void => {
    this.pressing = false;
  };

  /** Builds the panel for the stage's current shown area (every control anew, the typed text kept). */
  private layout(): void {
    if (this.input) this.typed = this.input.text;
    const st = this.root.stage;
    // (the field being typed in keeps the focus in its new copy: the on-screen keyboard stays)
    const typing = !!st && !!this.input && st.focus === this.input;
    const L = (this.lay = layoutFor(st));
    const b = this.body;
    while (b.numChildren > 0) b.removeChildAt(0);
    b.x = L.x;
    b.y = L.y;
    b.scaleX = b.scaleY = L.u;
    // The frame: the cream edge, the light green border, the pale body (menu_tired's colours).
    const frame = new Sprite();
    frame.name = '$chat_frame';
    rect(frame.graphics, EDGE, 0, 0, L.w, L.h);
    rect(frame.graphics, BORDER_MID, 3, 3, L.w - 6, L.h - 6);
    rect(frame.graphics, BORDER, 4, 4, L.w - 8, L.h - 8);
    rect(frame.graphics, BODY, 9, 9, L.w - 18, L.h - 18);
    b.addChild(frame);
    this.decorate(b, L);

    // The header: the tabs, the hidden players, the close button.
    const pad = 16;
    const tabW = L.wide ? 132 : 120;
    const tabH = L.row - 8;
    this.tabs = [];
    this.order.forEach((channel, i) => {
      const t = new PlateButton(`chat_tab_${i === 0 ? 'global' : 'clan'}`, i === 0 ? T.MSG_CHAT_GLOBAL : T.MSG_CHAT_CLAN, { w: tabW, h: tabH, size: L.nick });
      t.x = pad + i * (tabW + 10);
      t.y = 12;
      t.setHitRect({ x: -5, y: -6, width: tabW + 10, height: L.header - 4 });
      t.addEventListener(MouseEvent.CLICK, () => this.switchTo(channel));
      b.addChild(t);
      this.tabs.push(t);
    });
    const hiddenN = this.ctl.hidden().length;
    this.hiddenButton = null;
    const closeSize = L.row - 4;
    if (hiddenN > 0 || this.mode === 'hidden') {
      const hw = L.wide ? 170 : 150;
      const hb = new PlateButton('chat_hidden', compose(T.TPL_CHAT_HIDDEN_LIST, { n: hiddenN }).text, { w: hw, h: tabH, size: L.meta + 1, style: 'light', raw: true });
      hb.x = L.w - pad - closeSize - 12 - hw;
      hb.y = 12;
      hb.setHitRect({ x: -5, y: -6, width: hw + 10, height: L.header - 4 });
      hb.addEventListener(MouseEvent.CLICK, () => this.showMode(this.mode === 'hidden' ? 'messages' : 'hidden'));
      b.addChild(hb);
      this.hiddenButton = hb;
    }
    this.closeButton = this.closeOf(closeSize);
    this.closeButton.x = L.w - pad - closeSize + 4;
    this.closeButton.y = 8;
    b.addChild(this.closeButton);

    // The messages.
    const inputTop = L.h - 12 - 18 - L.row;
    this.listX = pad;
    this.listY = L.header + 8;
    this.listW = L.w - 2 * pad;
    this.listH = inputTop - 8 - this.listY;
    this.list = new Sprite();
    this.list.name = 'chat_list';
    this.list.x = this.listX;
    this.list.y = this.listY;
    this.list.scrollRect = new Rectangle(0, 0, this.listW, this.listH);
    this.listBg = new Sprite();
    rect(this.listBg.graphics, LIST_BG, 0, 0, this.listW, this.listH);
    this.list.addChild(this.listBg);
    this.content = new Sprite();
    this.content.mouseEnabled = false;
    this.content.mouseChildren = false;
    this.list.addChild(this.content);
    // The list takes the mouse where it is shown only: its rows above and below the window are hit-tested too, and
    // the scrolled-away rows would take the taps of the tabs and the field (hitArea: the window's rectangle).
    const window = new Sprite();
    window.name = '$chat_list_hit';
    window.mouseEnabled = false;
    rect(window.graphics, 0, this.listX, this.listY, this.listW, this.listH, 0, 0);
    b.addChild(window);
    this.list.hitArea = window;
    this.list.addEventListener(MouseEvent.MOUSE_DOWN, this.onDown);
    this.list.addEventListener(MouseEvent.MOUSE_WHEEL, this.onWheel);
    const listFrame = new Shape();
    listFrame.graphics.lineStyle(1, BORDER, 1);
    listFrame.graphics.drawRect(this.listX - 0.5, this.listY - 0.5, this.listW + 1, this.listH + 1);
    b.addChild(this.list);
    b.addChild(listFrame);
    this.scrollBar = new Shape();
    b.addChild(this.scrollBar);
    this.rowCache.clear();
    this.rows = [];

    // The list of hidden players (in the list's place).
    this.hiddenPage = new Sprite();
    this.hiddenPage.name = 'chat_hidden_page';
    this.hiddenPage.x = this.listX;
    this.hiddenPage.y = this.listY;
    b.addChild(this.hiddenPage);

    // "New messages" over the list's bottom right corner.
    const pillW = L.wide ? 130 : 110;
    this.newPill = new PlateButton('chat_new', '', { w: pillW, h: L.row - 12, size: L.meta + 1, raw: true });
    this.newPill.x = this.listX + this.listW - pillW - 12;
    this.newPill.y = this.listY + this.listH - (L.row - 12) - 8;
    this.newPill.addEventListener(MouseEvent.CLICK, () => this.scrollToBottom());
    this.newPill.visible = false;
    b.addChild(this.newPill);

    // The input row: the field, its placeholder, "Отправить"; under it the status and the counter.
    const sendW = L.wide ? 132 : 128;
    const fieldW = this.listW - sendW - 10;
    const fieldH = L.row - 8;
    // The field: a white box of the row's height (a finger's target: a tap anywhere on it focuses the field) with the
    // one-line text field in its middle.
    this.fieldBox = new Sprite();
    this.fieldBox.name = 'chat_field';
    this.fieldBox.x = pad;
    this.fieldBox.y = inputTop + 4;
    this.fieldW = fieldW;
    this.fieldH = fieldH;
    this.drawFieldBox(false);
    this.fieldBox.addEventListener(MouseEvent.MOUSE_DOWN, () => {
      const st = this.root.stage;
      if (st && this.input.visible && st.focus !== this.input) st.focus = this.input;
    });
    b.addChild(this.fieldBox);
    this.input = inputField(CHAT_INPUT_NAME, { width: fieldW - 4, maxChars: 2 * CHAT_TEXT_MAX });
    const f = this.input.defaultTextFormat;
    f.size = L.text + 1;
    f.leftMargin = 4;
    f.rightMargin = 4;
    this.input.defaultTextFormat = f;
    this.input.border = false;
    this.input.background = false;
    this.input.height = Math.min(fieldH - 4, Math.ceil((L.text + 1) * 1.25 + 6));
    this.input.x = pad + 2;
    this.input.y = this.fieldBox.y + Math.round((fieldH - this.input.height) / 2);
    this.input.$enterKeyHint = 'send';
    this.input.$keyboardWith = CHAT_SEND_NAME;
    if (this.typed) this.input.replaceText(0, 0, this.typed);
    this.input.addEventListener(Event.CHANGE, () => this.updateInputRow());
    b.addChild(this.input);
    this.placeholder = line(L.text, HINT_COLOR);
    this.placeholder.text = T.MSG_CHAT_PLACEHOLDER;
    this.placeholder.x = pad + 8;
    this.placeholder.y = this.fieldBox.y + (fieldH - this.placeholder.height) / 2;
    b.addChild(this.placeholder);
    this.sendButton = new PlateButton(CHAT_SEND_NAME, T.MSG_CHAT_SEND, { w: sendW, h: L.row, size: L.nick, style: 'go' });
    this.sendButton.x = L.w - pad - sendW;
    this.sendButton.y = inputTop;
    this.sendButton.setHitRect({ x: -5, y: -4, width: sendW + 5 + pad - 4, height: L.row + 8 });
    this.sendButton.addEventListener(MouseEvent.CLICK, () => this.submit());
    b.addChild(this.sendButton);
    this.note = block(L.text, TEXT_COLOR, this.listW, true, 'center');
    this.note.name = 'chat_note';
    this.note.x = pad;
    this.note.y = inputTop + 6;
    b.addChild(this.note);
    // The question about a message of another player (in the input row's place).
    const hideW = Math.round(this.listW * 0.66);
    this.hideButton = new PlateButton('chat_hide_player', '', { w: hideW, h: L.row, size: L.nick, raw: true });
    this.hideButton.x = pad;
    this.hideButton.y = inputTop;
    this.hideButton.addEventListener(MouseEvent.CLICK, () => this.hideSelected());
    b.addChild(this.hideButton);
    this.cancelButton = new PlateButton('chat_hide_cancel', T.MSG_CHAT_CANCEL, { w: this.listW - hideW - 10, h: L.row, size: L.nick, style: 'light' });
    this.cancelButton.x = pad + hideW + 10;
    this.cancelButton.y = inputTop;
    this.cancelButton.addEventListener(MouseEvent.CLICK, () => this.select(null));
    b.addChild(this.cancelButton);
    this.statusField = line(L.meta + 1, ERROR_COLOR, true);
    this.statusField.name = 'chat_status';
    this.statusField.x = pad;
    this.statusField.y = L.h - 12 - 17;
    b.addChild(this.statusField);
    this.counter = line(L.meta + 1, META);
    this.counter.name = 'chat_counter';
    this.counter.y = L.h - 12 - 17;
    b.addChild(this.counter);

    this.showTabs();
    this.renderList(true);
    this.renderHidden();
    this.showMode(this.mode);
    this.updateInputRow();
    if (typing && st && this.input.visible) st.focus = this.input;
  }

  /** The field's white box, its edge in the links' green while the field has the focus. */
  private drawFieldBox(focused: boolean): void {
    this.fieldFocused = focused;
    const g = this.fieldBox.graphics;
    g.clear();
    rect(g, focused ? LINK_COLOR : 0x82a852, 0, 0, this.fieldW, this.fieldH, 6);
    rect(g, 0xffffff, focused ? 2 : 1, focused ? 2 : 1, this.fieldW - (focused ? 4 : 2), this.fieldH - (focused ? 4 : 2), 5);
  }

  /** The beanstalk and the bones of the game's popup frame, at the panel's corners as menu_tired has them. */
  private decorate(b: Sprite, L: Layout): void {
    const tired = this.fwp && this.fwp.container_interf && this.fwp.container_interf.get_object('menu_tired');
    if (!tired) return;
    let frame: any = null;
    const kids: any[] = [];
    for (let i = 0; i < tired.numChildren; i++) kids.push(tired.getChildAt(i));
    for (const c of kids) {
      const r = c.getBounds(tired);
      if (!frame || r.width * r.height > frame.r.width * frame.r.height) frame = { c, r };
    }
    if (!frame) return;
    const fr = frame.r;
    const k = L.wide ? Math.min(1, L.h / 480) : 0.9;
    for (const c of kids) {
      if (c === frame.c || c instanceof TextField || c.$isButton || (c.getBounds(tired).width < 40 && c.getBounds(tired).height < 40)) continue;
      const r = c.getBounds(tired);
      tired.removeChild(c);
      c.mouseEnabled = false;
      if ('mouseChildren' in c) c.mouseChildren = false;
      c.scaleX = c.scaleY = k;
      const nb = c.getBounds(c);
      // Outside the frame, clear of the messages and the field: the beanstalk climbs the right edge from under the
      // header, the bones lean on the bottom left corner.
      if (r.x + r.width / 2 > fr.x + fr.width / 2) {
        c.x = L.w - 6 - nb.x * k;
        c.y = L.header - nb.y * k;
      } else {
        c.x = 6 - nb.width * k - nb.x * k;
        c.y = L.h - nb.height * k - nb.y * k;
      }
      b.addChild(c);
    }
  }

  /** The close button: a plate of the main map's colours with a cross, a finger's target. */
  private closeOf(size: number): any {
    const x = new PlateButton('chat_close', '\u00d7', { w: size, h: size, size: Math.round(size * 0.62), raw: true });
    x.setHitRect({ x: -6, y: -6, width: size + 12, height: size + 12 });
    x.addEventListener(MouseEvent.CLICK, () => this.close());
    return x;
  }

  // ------------------------------------------------------------------------------------------------ channels

  private viewOf(info: ChatChannelInfo): ChannelView {
    let v = this.views.get(info.channel);
    if (!v) this.views.set(info.channel, (v = { info, messages: [], older: undefined, loading: false, failed: false, fresh: 0 }));
    else v.info = info;
    return v;
  }

  /** chat.channels: the tabs, the unread counts, whether the player writes; then subscribe and load what is shown. */
  private refreshChannels(first = false): void {
    const s = this.services;
    if (!s || typeof s.chatChannels !== 'function') return;
    Promise.resolve(s.chatChannels(this.ctl.hidden().map((h) => h.id))).then(
      (r: { channels: ChatChannelInfo[]; mutedUntil: number | null }) => {
        if (this.disposed) return;
        this.ctl.adopt(r);
        const order = r.channels.map((c) => c.channel);
        for (const c of r.channels) {
          const v = this.viewOf(c);
          if (first && c.channel !== this.current) v.fresh = c.unread;
        }
        for (const id of [...this.views.keys()]) if (!order.includes(id)) this.views.delete(id);
        const changed = order.join('|') !== this.order.join('|');
        this.order = order.length > 0 ? order : [CHAT_GLOBAL];
        if (!this.order.includes(this.current)) this.current = CHAT_GLOBAL;
        if (changed && !this.pressing) this.layout();
        else {
          if (changed) this.held = 'always';
          this.showTabs();
          this.updateInputRow();
        }
        const channels = this.order;
        Promise.resolve(this.ctl.unsubscribing)
          .then(() => (this.disposed ? undefined : s.chatSubscribe(channels)))
          .catch(() => undefined);
        const v = this.views.get(this.current);
        if (v && v.older === undefined && !v.loading) this.loadPage(this.current);
        else if (v && !first) this.reloadNewest(this.current);
      },
      (error: any) => {
        if (this.disposed) return;
        if (netErrorCode(error) === 'UNAUTHORIZED') return this.endSession();
        this.setStatus(T.MSG_CHAT_LOAD_FAILED, true);
      },
    );
  }

  private switchTo(channel: string): void {
    if (this.disposed || channel === this.current && this.mode === 'messages') return;
    this.select(null);
    this.current = channel;
    const v = this.views.get(channel);
    if (v) v.fresh = 0;
    this.showMode('messages');
    this.showTabs();
    this.renderList(true);
    this.updateInputRow();
    if (v && v.older === undefined && !v.loading) this.loadPage(channel);
    else this.noteRead();
  }

  private showTabs(): void {
    this.tabs.forEach((t, i) => {
      const channel = this.order[i];
      const v = channel ? this.views.get(channel) : undefined;
      t.visible = !!channel;
      t.setStyle(channel === this.current && this.mode === 'messages' ? 'dark' : 'light');
      t.setBadge(channel === this.current ? 0 : (v?.fresh ?? 0), this.lay.meta);
      if (v && v.info.kind === 'clan' && v.info.clan) {
        // "Клан" with the clan's name: data after game text.
        t.setText(`${translate(T.MSG_CHAT_CLAN)}: ${v.info.clan.name}`, true);
      }
    });
  }

  /** The newest page (the first load of a channel), or an older one at the top. */
  private loadPage(channel: string, older = false): void {
    const v = this.views.get(channel);
    const s = this.services;
    if (!v || v.loading || !s || (older && !v.older)) return;
    v.loading = true;
    v.failed = false;
    if (channel === this.current) this.renderList(false);
    Promise.resolve(s.chatHistory(channel, older && v.older ? { cursor: v.older } : {})).then(
      (page: { items: ChatMessage[]; next: string | null }) => {
        v.loading = false;
        if (this.disposed) return;
        const before = this.contentHeight;
        // (a list at its bound asks for nothing older: the server keeps no more either)
        v.older = this.merge(v, page.items) ? null : page.next;
        if (channel !== this.current) return;
        if (older) {
          this.renderList(false);
          // (the rows the reader looked at stay where they were)
          this.scrollTo(this.scrollY + (this.contentHeight - before));
        } else {
          this.renderList(true);
          this.noteRead();
        }
      },
      (error: any) => {
        v.loading = false;
        if (this.disposed) return;
        if (netErrorCode(error) === 'UNAUTHORIZED') return this.endSession();
        v.failed = true;
        if (netErrorCode(error) === 'FORBIDDEN') this.refreshChannels();
        if (channel === this.current) {
          this.renderList(false);
          this.setStatus(T.MSG_CHAT_LOAD_FAILED, true);
        }
      },
    );
  }

  /** After a reconnect or a gap: the newest page again, merged with what is shown. */
  private reloadNewest(channel: string): void {
    const v = this.views.get(channel);
    const s = this.services;
    if (!v || !s) return;
    Promise.resolve(s.chatHistory(channel, {})).then(
      (page: { items: ChatMessage[]; next: string | null }) => {
        if (this.disposed) return;
        const known = new Set(page.items.map((m) => m.messageId));
        const oldest = page.items.length > 0 ? page.items[page.items.length - 1].seq : Number.POSITIVE_INFINITY;
        // (what fell out of the newest page's range stays; inside it the server's page is the truth)
        v.messages = v.messages.filter((m) => m.seq < oldest || known.has(m.messageId));
        this.merge(v, page.items);
        if (channel === this.current) {
          this.renderList(this.atBottom);
          this.noteRead();
        }
      },
      () => undefined,
    );
  }

  /** Merges messages into a channel's list (by id, oldest first); true when the list's bound dropped older ones. */
  private merge(v: ChannelView, items: ChatMessage[]): boolean {
    const byId = new Map(v.messages.map((m) => [m.messageId, m]));
    for (const m of items) if (m && typeof m.messageId === 'string') byId.set(m.messageId, m);
    v.messages = [...byId.values()].sort((a, b) => a.seq - b.seq);
    // A panel left open on a busy channel keeps what the server keeps (its newest messages), not every message since it
    // opened: the older ones are gone from the server too, so nothing older is asked for after them.
    const keep = this.keptIn(v);
    if (v.messages.length <= keep) return false;
    v.messages = v.messages.slice(v.messages.length - keep);
    if (v.older !== undefined) v.older = null;
    return true;
  }

  /** How many messages a channel's list keeps: the server's history of the channel (limits.chat), at least 50. */
  private keptIn(v: ChannelView): number {
    const s = this.services;
    const limits = s && typeof s.chatLimits === 'function' ? s.chatLimits() : null;
    const n = Number(limits && (v.info.kind === 'clan' ? limits.historyClan : limits.historyGlobal));
    return Number.isFinite(n) && n > 0 ? Math.max(50, Math.floor(n)) : CHAT_KEPT_DEFAULT;
  }

  // ------------------------------------------------------------------------------------------------ events

  private readonly onFrame = (): void => {
    if (this.disposed) return;
    const st = this.root.stage;
    // Not over a battle or another screen: the chat belongs to the main map.
    const i = this.fwp._interf;
    const top = i && i.cur_screen ? i.cur_screen[i.cur_screen[0]] : null;
    if (this.fwp.main_mode !== 'IDLE' || (top && top.name !== 'mainframe') || !this.fwp.user || !this.fwp.user.player_id) {
      this.close();
      return;
    }
    if (st && !this.stageRef) this.attach();
    if (this.held && !this.pressing) {
      const held = this.held;
      this.held = null;
      if (held === 'always') this.layout();
      else this.onResize();
    }
    const q = this.queue;
    this.queue = [];
    for (const e of q) this.apply(e);
    if (this.status && this.status.until > 0 && Date.now() > this.status.until) this.setStatus('', false);
    if (this.markTimer && Date.now() >= this.markTimer.at) this.flushRead();
    if (this.input) {
      const focused = !!st && st.focus === this.input;
      this.placeholder.visible = this.input.visible && !focused && this.input.text === '';
      if (focused !== this.fieldFocused) this.drawFieldBox(focused);
    }
  };

  private apply(e: Queued): void {
    if (e.kind === 'membership') return this.refreshChannels();
    if (e.kind === 'status') {
      if (e.status === 'open') this.refreshChannels();
      else if (e.status === 'reconnecting') this.setStatus(T.MSG_CHAT_OFFLINE, true, 0);
      return;
    }
    if (e.kind === 'deleted') {
      const v = this.views.get(e.channel);
      if (!v) return;
      const gone = new Set(e.ids);
      const before = v.messages.length;
      v.messages = v.messages.filter((m) => !gone.has(m.messageId));
      if (this.selected && gone.has(this.selected.messageId)) this.select(null);
      if (e.channel === this.current && v.messages.length !== before) this.renderList(this.atBottom);
      return;
    }
    const m = e.message;
    const v = this.views.get(m.channel);
    if (!v || v.messages.some((x) => x.messageId === m.messageId)) return;
    // (a message older than the loaded range is in an older page: it is shown when that page loads)
    if (v.messages.length > 0 && m.seq < v.messages[0].seq && v.older) return;
    this.merge(v, [m]);
    const hidden = this.ctl.isHidden(m.playerId);
    const own = m.playerId === this.fwp.user.player_id;
    if (m.channel !== this.current || this.mode !== 'messages') {
      if (!hidden && !own) v.fresh++;
      this.showTabs();
      return;
    }
    const wasBottom = this.atBottom;
    this.renderList(wasBottom);
    if (!wasBottom && !hidden && !own) {
      this.unseen++;
      this.showPill();
    }
    if (wasBottom) this.noteRead();
  }

  // ------------------------------------------------------------------------------------------------ the list

  private shown(v: ChannelView): ChatMessage[] {
    const hidden = new Set(this.ctl.hidden().map((h) => h.id));
    return v.messages.filter((m) => !hidden.has(m.playerId));
  }

  /** Lays out the current channel's rows (each row built once); `bottom`: scrolls to the newest. */
  private renderList(bottom: boolean): void {
    const v = this.views.get(this.current);
    const L = this.lay;
    const c = this.content;
    while (c.numChildren > 0) c.removeChildAt(0);
    this.rows = [];
    const messages = v ? this.shown(v) : [];
    const pad = 8;
    let y = pad;
    const top = (text: string, color: number) => {
      const tf = block(L.text, color, this.listW - 2 * pad, false, 'center');
      tf.text = text;
      fitHeight(tf);
      tf.x = pad;
      tf.y = y;
      c.addChild(tf);
      y += tf.height + pad;
    };
    if (v && (v.loading || v.older === undefined) && messages.length === 0) top(T.MSG_CHAT_LOADING, HINT_COLOR);
    else if (v && v.loading) top(T.MSG_CHAT_LOADING, HINT_COLOR);
    else if (v && v.failed && messages.length === 0) top(T.MSG_CHAT_LOAD_FAILED, ERROR_COLOR);
    // (a player who cannot write here is not asked to write first)
    else if (messages.length === 0) top(this.sendBlock() ? T.MSG_CHAT_EMPTY_READ : T.MSG_CHAT_EMPTY, HINT_COLOR);
    const keep = new Set<string>();
    for (const m of messages) {
      let row = this.rowCache.get(m.messageId);
      if (!row || row.message !== m) {
        row = this.buildRow(m);
        this.rowCache.set(m.messageId, row);
      }
      keep.add(m.messageId);
      row.sprite.y = y;
      c.addChild(row.sprite);
      this.rows.push(row);
      y += row.height + 2;
    }
    for (const id of [...this.rowCache.keys()]) if (!keep.has(id)) this.rowCache.delete(id);
    this.contentHeight = y + pad - 2;
    // Few messages hug the bottom (the newest above the field), as in every chat.
    c.y = Math.max(0, this.listH - this.contentHeight);
    this.highlight();
    if (bottom) this.scrollToBottom();
    else this.scrollTo(this.scrollY);
  }

  private buildRow(m: ChatMessage): Row {
    const L = this.lay;
    const pad = 8;
    const w = this.listW - 2 * pad - 6;
    const s = new Sprite();
    s.name = `chat_msg_${m.messageId}`;
    const own = m.playerId === this.fwp.user.player_id;
    const nick = line(L.nick, own ? OWN_NICK : NICK, true);
    setRawText(nick, playerName(m.nickname));
    nick.x = pad;
    nick.y = 2;
    s.addChild(nick);
    const meta = line(L.meta, META);
    const parts = [compose(T.TPL_CHAT_LEVEL_SHORT, { level: m.level }).text];
    if (m.clan) parts.push(`[${m.clan.name}]`);
    parts.push(timeText(m.at));
    setRawText(meta, parts.join(' · '));
    meta.x = Math.round(nick.x + nick.textWidth + 10);
    meta.y = nick.y + Math.max(0, nick.height - meta.height) - 1;
    let below = nick.y + nick.height - 2;
    if (meta.x + meta.textWidth > pad + w) {
      // A long nickname and clan name: the level, clan and time go under the nickname (never cut off at the edge).
      meta.x = pad;
      meta.y = below;
      below = meta.y + meta.height - 2;
    }
    s.addChild(meta);
    const text = block(L.text, TEXT_COLOR, w);
    setRawText(text, m.text);
    fitHeight(text);
    text.x = pad;
    text.y = below;
    s.addChild(text);
    const height = Math.ceil(text.y + text.height + 2);
    const bg = new Shape();
    bg.name = 'chat_row_bg';
    rect(bg.graphics, own ? 0xf3ead2 : 0xffffff, 3, 0, this.listW - 2 * pad + 4, height, 10, own ? 0.6 : 0.5);
    s.addChildAt(bg, 0);
    return { sprite: s, message: m, height };
  }

  private highlight(): void {
    for (const r of this.rows) {
      const bg = r.sprite.getChildByName('chat_row_bg');
      if (bg) bg.alpha = this.selected && this.selected.messageId === r.message.messageId ? 1 : 0.85;
      r.sprite.alpha = this.selected && this.selected.messageId !== r.message.messageId ? 0.55 : 1;
    }
  }

  private maxScroll(): number {
    return Math.max(0, this.contentHeight - this.listH);
  }

  private scrollTo(y: number): void {
    this.scrollY = Math.max(0, Math.min(this.maxScroll(), Math.round(y)));
    this.list.scrollRect = new Rectangle(0, this.scrollY, this.listW, this.listH);
    this.listBg.y = this.scrollY;
    this.atBottom = this.scrollY >= this.maxScroll() - 4;
    if (this.atBottom && this.unseen > 0) {
      this.unseen = 0;
      this.noteRead();
    }
    this.showPill();
    this.drawScrollBar();
    const v = this.views.get(this.current);
    if (v && v.older && !v.loading && this.scrollY < LOAD_OLDER_AT && this.mode === 'messages') this.loadPage(this.current, true);
  }

  scrollToBottom(): void {
    this.unseen = 0;
    this.scrollTo(this.maxScroll());
    this.noteRead();
  }

  private showPill(): void {
    if (!this.newPill) return;
    this.newPill.visible = this.unseen > 0 && !this.atBottom && this.mode === 'messages';
    if (this.newPill.visible) this.newPill.setText(`${compose(T.TPL_CHAT_NEW, { n: this.unseen }).text} ↓`, true);
  }

  private drawScrollBar(): void {
    const g = this.scrollBar.graphics;
    g.clear();
    const max = this.maxScroll();
    if (max <= 0 || this.mode !== 'messages') return;
    const h = Math.max(24, (this.listH * this.listH) / this.contentHeight);
    const y = this.listY + ((this.listH - h) * this.scrollY) / max;
    rect(g, PLATE, this.listX + this.listW - 6, y, 4, h, 4, 0.6);
  }

  private localY(e: any): number {
    const p = this.body.globalToLocal(new Point(e.stageX, e.stageY));
    return p.y;
  }

  private readonly onDown = (e: any): void => {
    if (this.disposed) return;
    this.drag = { y: this.localY(e), scroll: this.scrollY, moved: false };
  };

  private readonly onMove = (e: any): void => {
    if (!this.drag || this.disposed) return;
    const dy = this.localY(e) - this.drag.y;
    if (!this.drag.moved && Math.abs(dy) < DRAG_SLOP) return;
    this.drag.moved = true;
    this.scrollTo(this.drag.scroll - dy);
  };

  private readonly onUp = (e: any): void => {
    const d = this.drag;
    this.drag = null;
    if (!d || d.moved || this.disposed || this.mode !== 'messages') return;
    // A tap: the row under it (in the content's coordinates).
    const y = this.localY(e) - this.listY + this.scrollY - this.content.y;
    const row = this.rows.find((r) => y >= r.sprite.y && y < r.sprite.y + r.height);
    if (!row) return this.select(null);
    if (row.message.playerId === this.fwp.user.player_id) return this.select(null);
    this.select(this.selected && this.selected.messageId === row.message.messageId ? null : row.message);
  };

  private readonly onWheel = (e: any): void => {
    if (this.disposed) return;
    this.scrollTo(this.scrollY - (Number(e.delta) || 0) * 24);
  };

  // ------------------------------------------------------------------------------------------------ the input row

  /** Why the player cannot write in the current channel (null: they can). */
  private sendBlock(): ChatChannelInfo['sendBlock'] {
    const v = this.views.get(this.current);
    const info = v?.info ?? this.ctl.channels.find((c) => c.channel === this.current) ?? null;
    return info ? info.sendBlock : null;
  }

  /** Shows what the input row holds now: the field and "Отправить", a note, or the question about a message. */
  private updateInputRow(): void {
    if (!this.input || this.disposed) return;
    const block = this.sendBlock();
    const asking = !!this.selected && this.mode === 'messages';
    const writing = !asking && this.mode === 'messages' && !block;
    this.input.visible = writing;
    this.fieldBox.visible = writing;
    this.sendButton.visible = writing;
    this.counter.visible = writing;
    this.hideButton.visible = asking;
    this.cancelButton.visible = asking;
    this.note.visible = !asking && this.mode === 'messages' && !!block;
    if (this.note.visible) {
      if (block === 'guest') this.note.text = T.MSG_CHAT_GUEST;
      else if (block === 'level') setRawText(this.note, compose(T.TPL_CHAT_LEVEL, { level: this.levelToSend() }).text);
      else setRawText(this.note, compose(T.TPL_CHAT_MUTED, { time: timeText(this.ctl.mutedUntil ?? Date.now(), true) }).text);
    }
    if (!writing && this.stageRef && this.stageRef.focus === this.input) this.stageRef.focus = null;
    const n = chatTextLength(sanitizeChatText(this.input.text));
    setRawText(this.counter, `${n}/${CHAT_TEXT_MAX}`);
    this.counter.textColor = n > CHAT_TEXT_MAX ? ERROR_COLOR : META;
    this.counter.x = this.listX + this.listW - this.counter.width;
    this.sendButton.enabled = !this.busy && n > 0 && n <= CHAT_TEXT_MAX;
    if (asking && this.selected) this.hideButton.setText(compose(T.TPL_CHAT_HIDE, { name: playerName(this.selected.nickname) }).text, true);
    if (!this.placeholder) return;
    this.placeholder.visible = writing && this.input.text === '';
  }

  private levelToSend(): number {
    const s = this.services;
    const limits = s && typeof s.chatLimits === 'function' ? s.chatLimits() : null;
    return limits ? Number(limits.minLevelToSend) : 1;
  }

  private readonly onKey = (e: any): void => {
    if (this.disposed) return;
    if (e.keyCode === 27) this.back();
    else if (e.keyCode === 13 && this.stageRef && this.stageRef.focus === this.input) this.submit();
  };

  /** "Отправить": the text as the server will see it, then chat.send; one request at a time. */
  submit(): void {
    if (this.disposed || this.busy || !this.input.visible) return;
    const text = sanitizeChatText(this.input.text);
    const n = chatTextLength(text);
    if (n === 0) return this.setStatus(T.MSG_CHAT_TEXT_EMPTY, true);
    if (n > CHAT_TEXT_MAX) return this.setStatus(compose(T.TPL_CHAT_TEXT_TOO_LONG, { max: CHAT_TEXT_MAX }).text, true, undefined, true);
    const channel = this.current;
    this.busy = true;
    this.sent++;
    this.updateInputRow();
    Promise.resolve(this.services.chatSend(channel, text)).then(
      (m: ChatMessage) => {
        this.busy = false;
        if (this.disposed) return;
        this.input.text = '';
        this.setStatus('', false);
        const v = this.views.get(m.channel);
        if (v) this.merge(v, [m]);
        if (m.channel === this.current) this.renderList(true);
        this.readSent.set(m.channel, Math.max(this.readSent.get(m.channel) ?? 0, m.seq));
        this.updateInputRow();
      },
      (error: any) => {
        this.busy = false;
        if (this.disposed) return;
        this.updateInputRow();
        this.sendFailed(error);
      },
    );
  }

  private sendFailed(error: any): void {
    const code = netErrorCode(error);
    const d = error && error.details ? error.details : {};
    const reason = typeof d.reason === 'string' ? d.reason : '';
    if (code === 'UNAUTHORIZED') return this.endSession();
    if (reason === 'chat_rate_limited') return this.setStatus(T.MSG_CHAT_TOO_FAST, true);
    if (reason === 'chat_duplicate') return this.setStatus(T.MSG_CHAT_DUPLICATE, true);
    if (reason === 'chat_text_empty') return this.setStatus(T.MSG_CHAT_TEXT_EMPTY, true);
    if (reason === 'chat_text_too_long') return this.setStatus(compose(T.TPL_CHAT_TEXT_TOO_LONG, { max: Number(d.max) || CHAT_TEXT_MAX }).text, true, undefined, true);
    if (reason === 'chat_muted' || reason === 'guest_not_allowed' || reason === 'chat_level_too_low') {
      if (reason === 'chat_muted' && typeof d.until === 'number') this.ctl.mutedUntil = d.until;
      return this.refreshChannels();
    }
    if (reason === 'chat_not_member') {
      this.setStatus(T.MSG_CHAT_NOT_MEMBER, true);
      return this.refreshChannels();
    }
    this.setStatus(T.MSG_CHAT_SEND_FAILED, true);
  }

  /** A line under the field: an error (red) or a notice; it goes after a while (0: stays until replaced). */
  private setStatus(text: string, error: boolean, ms = 5_000, raw = false): void {
    if (!this.statusField) return;
    this.status = text ? { text, error, until: ms > 0 ? Date.now() + ms : 0 } : null;
    this.statusField.textColor = error ? ERROR_COLOR : LINK_COLOR;
    if (!text) this.statusField.text = '';
    else if (raw) setRawText(this.statusField, text);
    else this.statusField.text = text;
  }

  // ------------------------------------------------------------------------------------------------ read positions

  /** The newest shown message counts as read soon (chat.markRead once the list settled). */
  private noteRead(): void {
    const v = this.views.get(this.current);
    if (!v || v.messages.length === 0 || !this.atBottom || this.mode !== 'messages') return;
    const seq = v.messages[v.messages.length - 1].seq;
    if ((this.readSent.get(this.current) ?? 0) >= seq) return;
    this.markTimer = { channel: this.current, seq, at: Date.now() + MARK_READ_MS };
  }

  private flushRead(): void {
    const t = this.markTimer;
    this.markTimer = null;
    const s = this.services;
    if (!t || !s || typeof s.chatMarkRead !== 'function') return;
    if ((this.readSent.get(t.channel) ?? 0) >= t.seq) return;
    this.readSent.set(t.channel, t.seq);
    Promise.resolve(s.chatMarkRead(t.channel, t.seq)).catch(() => undefined);
    const c = this.ctl.channels.find((x) => x.channel === t.channel);
    if (c) c.unread = 0;
  }

  // ------------------------------------------------------------------------------------------------ hiding players

  private select(m: ChatMessage | null): void {
    this.selected = m;
    this.highlight();
    this.updateInputRow();
  }

  private hideSelected(): void {
    const m = this.selected;
    if (!m) return;
    const name = playerName(m.nickname);
    this.ctl.hide(m.playerId, name);
    this.selected = null;
    this.layout();
    this.setStatus(compose(T.TPL_CHAT_HIDDEN_DONE, { name }).text, false, 5_000, true);
  }

  private showMode(mode: 'messages' | 'hidden'): void {
    this.mode = mode;
    if (mode === 'hidden') this.select(null);
    this.list.visible = mode === 'messages';
    this.hiddenPage.visible = mode === 'hidden';
    if (mode === 'hidden') this.renderHidden();
    this.showTabs();
    this.showPill();
    this.drawScrollBar();
    this.updateInputRow();
    if (this.hiddenButton) this.hiddenButton.setStyle(mode === 'hidden' ? 'dark' : 'light');
    if (mode === 'messages') this.renderList(true);
  }

  private renderHidden(): void {
    const p = this.hiddenPage;
    while (p.numChildren > 0) p.removeChildAt(0);
    const L = this.lay;
    rect(p.graphics, LIST_BG, 0, 0, this.listW, this.listH);
    const list = this.ctl.hidden();
    let y = 10;
    const hint = block(L.meta + 1, HINT_COLOR, this.listW - 20);
    hint.text = list.length === 0 ? T.MSG_CHAT_HIDDEN_NONE : T.MSG_CHAT_HIDDEN_HINT;
    fitHeight(hint);
    hint.x = 10;
    hint.y = y;
    p.addChild(hint);
    y += hint.height + 6;
    const bw = L.wide ? 120 : 110;
    for (const h of list.slice().reverse()) {
      if (y + L.row > this.listH) break;
      const name = line(L.nick, NICK, true);
      setRawText(name, h.name);
      name.x = 12;
      name.y = y + (L.row - name.height) / 2;
      p.addChild(name);
      const show = new PlateButton(`chat_unhide_${h.id}`, T.MSG_CHAT_SHOW_AGAIN, { w: bw, h: L.row - 6, size: L.meta + 1, style: 'light' });
      show.x = this.listW - bw - 12;
      show.y = y + 3;
      show.addEventListener(MouseEvent.CLICK, () => {
        this.ctl.unhide(h.id);
        this.rowCache.clear();
        this.layout();
      });
      p.addChild(show);
      y += L.row + 4;
    }
  }

  private endSession(): void {
    const ended = this.opts.onSessionEnded;
    this.close();
    if (ended) ended();
  }
}

/** HH:MM of today, else DD.MM HH:MM (`always`: with the date when not today, for a mute's end). */
export function timeText(at: number, withDate = false): string {
  const d = new Date(at);
  const now = new Date();
  const two = (n: number) => String(n).padStart(2, '0');
  const hm = `${two(d.getHours())}:${two(d.getMinutes())}`;
  const today = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  return today && !withDate ? hm : today ? hm : `${two(d.getDate())}.${two(d.getMonth() + 1)} ${hm}`;
}
